import { Button } from "@/components/ui/baseUi/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/baseUi/command"
import { PhoneNumberInput } from "@/components/ui/forms/phoneNumberInput"
import { StandardInput } from "@/components/ui/forms/standardInput"
import { TextAreaInput } from "@/components/ui/forms/textareaInput"
import { Label } from "@/components/ui/baseUi/label"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/baseUi/popover"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { removeDiacritics } from "@/modules/utils/stringUtils"
import { type AddressAutofillRetrieveResponse } from "@mapbox/search-js-core"
import { AddressAutofill } from "@mapbox/search-js-react"
import { Config } from "@modules/config"
import { word } from "@modules/core/utils/i18n"
import { useCountriesListArray } from "@modules/language/utils/countries"
import { type ProfileFormTabProps } from "@modules/profile/myProfile/profileFormikType"
import type { TCountryCode } from "countries-list"
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react"
import { useEffect, useState } from "react"

export function ContactForm({ getFormErrorMessage, formik }: ProfileFormTabProps) {
  const countryOptions = useCountriesListArray()
  const [open, setOpen] = useState(false)

  useEffect(() => {
    // Init the phone country code with address' country code
    if (formik.values.country && !formik.values.phoneNumberCountryCode)
      formik.setFieldValue("phoneNumberCountryCode", formik.values.country)
  }, [formik.values.country])

  return (
    <div className="mt-6 flex grow flex-col justify-start gap-4 px-4">
      <Text fontStyle="TM" className="self-center">
        {word("profile.form.contact.title")}
      </Text>

      <Text
        fontStyle="BS"
        className="mb-4 text-center"
        style={{ whiteSpace: "pre-line", overflowWrap: "break-word" }}
        color="var(--neutral-600)"
      >
        {word("profile.form.contact.subtitle")}
      </Text>

      {/* Email input */}
      <StandardInput
        name="email"
        label={word("profile.form.mail.label")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        error={getFormErrorMessage("email")}
        inputProps={{
          disabled: true,
        }}
      />

      <AddressAutofill
        accessToken={Config.MAPBOX_API_KEY}
        onRetrieve={(res: AddressAutofillRetrieveResponse) => {
          const foundCountry = countryOptions.find(
            (countryOption) => countryOption.code === res.features[0]!.properties.country_code?.toUpperCase(),
          )
          if (foundCountry) formik.setFieldValue("country", foundCountry.code)
        }}
        theme={{
          variables: {
            unitHeader: "28px",
            fontFamily: '"Trenda", sans-serif',
            borderRadius: "0",
            boxShadow: "0 0 1px 1px rgba(0,0,0,0.1)",
          },
          cssText: `
            .Results {
                z-index: 100000;
                border-radius: 20px;
            }
            .ResultsAttribution {
                  display: none;
                }
        `,
        }}
        popoverOptions={{ offset: 0 }}
      >
        {/* Address input */}
        <StandardInput
          name="address"
          label={word("box.form.address.address.label")}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("address")}
          inputProps={{
            autoComplete: "address-line1",
            placeholder: word("box.form.address.address.label"),
            onBlur: (e) => {
              if (formik.handleBlur) formik.handleBlur(e, "address")
            },
          }}
        />
      </AddressAutofill>

      <div className="flex flex-row gap-5">
        {/* Postal code input */}
        <StandardInput
          name="postalCode"
          label={word("box.form.address.postalCode.label")}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("postalCode")}
          inputProps={{
            autoComplete: "post-code",
            placeholder: word("box.form.address.postalCode.label"),
            onBlur: (e) => {
              if (formik.handleBlur) formik.handleBlur(e, "postalCode")
            },
          }}
        />

        {/* City input */}
        <StandardInput
          name="city"
          label={word("box.form.address.city.label")}
          setFieldValue={formik.setFieldValue}
          getFieldProps={formik.getFieldProps}
          error={getFormErrorMessage("city")}
          inputProps={{
            autoComplete: "address-level2",
            placeholder: word("box.form.address.city.label"),
            onBlur: (e) => {
              if (formik.handleBlur) formik.handleBlur(e, "city")
            },
          }}
        />
      </div>

      {/* Country input */}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="country">{word("box.form.address.country.label")}</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger
            render={
              <Button variant="outline" role="combobox" aria-expanded={open} className="h-11 w-full justify-between">
                {formik.values.country
                  ? countryOptions.find((countryOptions) => countryOptions.code === formik.values.country)?.label
                  : word("box.form.address.country.label")}

                <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            }
          />
          <PopoverContent className="w-65 border-0 p-0" align="start">
            <Command
              className="**:data-[slot=command-input-wrapper]:bg-neutral-50"
              filter={(value, search) => {
                if (removeDiacritics(value.toLowerCase()).includes(removeDiacritics(search.toLowerCase()))) return 1
                return 0
              }}
            >
              <CommandInput placeholder={word("global.searchCountry")} className="" />
              <CommandList>
                <CommandEmpty>{word("global.searchCountryNotFound")}</CommandEmpty>
                <CommandGroup>
                  {countryOptions.map((countryOption) => (
                    <CommandItem
                      key={countryOption.code}
                      value={countryOption.label}
                      onSelect={(currentValue) => {
                        const foundCountry = countryOptions.find((item) => item.label === currentValue)
                        formik.setFieldValue("country", foundCountry?.code)
                        setOpen(false)
                      }}
                      className="cursor-pointer hover:bg-neutral-200"
                    >
                      <CheckIcon
                        className={cn(
                          "mr-2 h-4 w-4",
                          formik.values.country === countryOption.code ? "opacity-100" : "opacity-0",
                        )}
                      />
                      {countryOption.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Phone number input */}
      <PhoneNumberInput
        label={word("profile.form.phoneNumber.label")}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        name="phoneNumber"
        countryCodeName="phoneNumberCountryCode"
        error={getFormErrorMessage("phoneNumber")}
        countryCode={formik.values.phoneNumberCountryCode as TCountryCode}
      />

      {/* Emergency contact input */}
      <TextAreaInput
        label={word("profile.form.emergency.label")}
        name="emergencyInformation"
        isRequiredInput={false}
        setFieldValue={formik.setFieldValue}
        getFieldProps={formik.getFieldProps}
        error={getFormErrorMessage("emergencyInformation")}
        textareaProps={{
          className: "field-sizing-fixed",
          placeholder: word("profile.form.emergency.placeholder"),
          rows: 3,
        }}
      />
    </div>
  )
}
